import React, { useState, useRef, useEffect } from "react";
import { Search, Sparkles, X, Building2, Leaf, AlertCircle, HelpCircle, Loader2 } from "lucide-react";
import { smartSearch, type AISearchResult } from "@/project/lib/ai-engine";
import { Link } from "@tanstack/react-router";

const typeIcons: Record<AISearchResult["type"], React.ReactNode> = {
  cause: <Leaf className="h-3.5 w-3.5 text-green-600" />,
  institution: <Building2 className="h-3.5 w-3.5 text-[var(--maroon)]" />,
  initiative: <Sparkles className="h-3.5 w-3.5 text-[var(--saffron-deep)]" />,
  faq: <HelpCircle className="h-3.5 w-3.5 text-blue-500" />,
};

const typeLabels: Record<AISearchResult["type"], string> = {
  cause: "Cause",
  institution: "Institution",
  initiative: "Initiative",
  faq: "FAQ",
};

const QUICK_PILLS = ["Gaushala", "80G Tax", "Fodder Drive", "Bird ICU", "Animal Seva", "Medicines"];

function ResultsDropdown({ results, loading, open, query, onClose }: {
  results: AISearchResult[];
  loading: boolean;
  open: boolean;
  query: string;
  onClose: () => void;
}) {
  if (!open) return null;
  if (results.length === 0 && !loading && !query) return null;

  return (
    <>
      {(results.length > 0 || loading) && (
        <div className="absolute left-0 right-0 top-full mt-2 z-50 rounded-2xl border border-border/50 bg-white shadow-2xl overflow-hidden">
          {loading && results.length === 0 && (
            <div className="flex items-center gap-2 px-4 py-3 text-sm text-foreground/60">
              <Loader2 className="h-4 w-4 animate-spin text-[var(--maroon)]" />
              Searching with AI...
            </div>
          )}
          {results.map((r, i) => (
            <Link
              key={i}
              to={r.href as "/"}
              onClick={onClose}
              className="flex items-start gap-3 px-4 py-3 hover:bg-[var(--maroon)]/5 transition border-b border-border/30 last:border-0 group"
            >
              <div className="mt-0.5 flex h-6 w-6 items-center justify-center rounded-lg bg-[var(--maroon)]/8 shrink-0">
                {typeIcons[r.type]}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-[var(--fg)] group-hover:text-[var(--maroon)] transition truncate">{r.title}</p>
                <p className="text-xs text-foreground/60 truncate mt-0.5">{r.subtitle}</p>
              </div>
              <span className="shrink-0 mt-0.5 rounded-full bg-border/50 px-1.5 py-0.5 text-[10px] text-foreground/60">
                {typeLabels[r.type]}
              </span>
            </Link>
          ))}
          <div className="flex items-center gap-1.5 px-4 py-2 bg-[var(--maroon)]/5">
            <Sparkles className="h-3 w-3 text-[var(--maroon)]" />
            <span className="text-[10px] text-foreground/60">Powered by Seva AI</span>
          </div>
        </div>
      )}
      {query && results.length === 0 && !loading && (
        <div className="absolute left-0 right-0 top-full mt-2 z-50 rounded-2xl border border-border/50 bg-white shadow-lg px-4 py-3">
          <div className="flex items-center gap-2 text-sm text-foreground/60">
            <AlertCircle className="h-4 w-4 text-amber-500" />
            No results for "{query}". Try "gaushala", "80G", or "fodder".
          </div>
        </div>
      )}
    </>
  );
}

export function AISearch({ compact = false }: { compact?: boolean }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<AISearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => {
    clearTimeout(timerRef.current);
    if (!query.trim()) { setResults([]); setLoading(false); return; }
    setLoading(true);
    timerRef.current = setTimeout(async () => {
      const r = await smartSearch(query);
      setResults(r);
      setLoading(false);
    }, 350);
    return () => clearTimeout(timerRef.current);
  }, [query]);

  useEffect(() => {
    function onClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  const handleClose = () => { setOpen(false); setQuery(""); };

  if (compact) {
    return (
      <div ref={containerRef} className="relative flex items-center gap-2 sm:gap-3 w-full">
        {/* Search pill */}
        <div className="relative flex flex-1 items-center gap-2 rounded-full border border-border/50 bg-white px-3.5 py-1.5 focus-within:ring-2 focus-within:ring-[var(--maroon)]/25 transition shadow-xs">
          {loading
            ? <Loader2 className="h-3.5 w-3.5 animate-spin text-[var(--maroon)] shrink-0" />
            : <Search className="h-3.5 w-3.5 text-foreground/40 shrink-0" />
          }
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
            onFocus={() => setOpen(true)}
            placeholder="Search institutions, causes, campaigns, 80G…"
            className="flex-1 bg-transparent text-xs outline-none placeholder:text-foreground/45 min-w-0"
            id="ai-search-input"
          />
          {query && (
            <button onClick={() => { setQuery(""); setResults([]); inputRef.current?.focus(); }}>
              <X className="h-3 w-3 text-foreground/50 hover:text-foreground transition" />
            </button>
          )}
          <span className="shrink-0 rounded-full bg-[var(--maroon)]/10 px-1.5 py-0.5 text-[10px] font-bold text-[var(--maroon)] flex items-center gap-0.5">
            <Sparkles className="h-2.5 w-2.5" />AI
          </span>
        </div>
        {/* Quick pills */}
        <div className="hidden md:flex items-center gap-1 shrink-0">
          {QUICK_PILLS.map((p) => (
            <button
              key={p}
              onClick={() => { setQuery(p); setOpen(true); inputRef.current?.focus(); }}
              className="shrink-0 rounded-full border border-border/60 bg-white px-2.5 py-1 text-[11px] font-medium text-foreground/65 hover:border-[var(--maroon)]/40 hover:text-[var(--maroon)] hover:bg-[var(--cream-2)] transition"
            >
              {p}
            </button>
          ))}
        </div>
        <ResultsDropdown results={results} loading={loading} open={open} query={query} onClose={handleClose} />
      </div>
    );
  }

  return (
    <div ref={containerRef} className="relative w-full">
      <div className="flex items-center gap-2 rounded-xl border border-border/50 bg-[var(--bg)] px-3 py-2 focus-within:ring-2 focus-within:ring-[var(--maroon)]/30 transition">
        {loading
          ? <Loader2 className="h-3.5 w-3.5 animate-spin text-[var(--maroon)] shrink-0" />
          : <Search className="h-3.5 w-3.5 text-foreground/40 shrink-0" />
        }
        <input
          ref={inputRef}
          value={query}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder="AI Search — institutions, causes, 80G…"
          className="flex-1 bg-transparent text-sm outline-none placeholder:text-foreground/40 min-w-0 w-0"
          id="ai-search-input"
        />
        {query && (
          <button onClick={() => { setQuery(""); setResults([]); inputRef.current?.focus(); }}>
            <X className="h-3 w-3 text-foreground/50 hover:text-foreground transition" />
          </button>
        )}
        <span className="shrink-0 rounded-full bg-[var(--maroon)]/10 px-1.5 py-0.5 text-[10px] font-bold text-[var(--maroon)]">
          AI
        </span>
      </div>
      <ResultsDropdown results={results} loading={loading} open={open} query={query} onClose={handleClose} />
    </div>
  );
}
